import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { adminSearchUserSchema } from '../../../forms/schemas/admin_tools'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { Icon, IconChoices } from '../../common/Icon'
import { ToastType } from '../../common/Toast/toast-type'
import { SearchBar } from '../../common/SearchBar'
import { adminQueries, loginAsUser } from '../../../api/admin'
import { getUserFullName } from '../../../helpers/user'
import { IUserModel } from '../../../interfaces/models/users.interfaces'
import {
  TWILIGHT_PREFIX,
  authQueries,
  setSecureBearerCookies,
  setSecureUserIdCookie,
} from '../../../helpers/authentication'
import { useAuth } from '../../../hooks/useAuth'

export const BoappaSuperadmin = (): React.ReactNode => {
  const { isBoappaSuperadmin } = useAuth()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const [selectedUser, setSelectedUser] = useState<IUserModel | null>(null)
  const [query, setQuery] = useState('')

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(adminSearchUserSchema),
    defaultValues: { query: '' },
  })

  const { data: listUsers, isFetching: isFetchingUsers } = useQuery(
    adminQueries.searchUser(query)
  )

  const onSubmit = async (
    data: InferType<typeof adminSearchUserSchema>
  ): Promise<void> => {
    setQuery(data.query)
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const onUserClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    user: IUserModel
  ): void => {
    e.preventDefault()
    setSelectedUser(user)
  }
  const navigate = useNavigate()

  const { mutate: mutateLoginAsUser, isPending: isLoggingInAsUser } =
    useMutation({
      mutationFn: async (selectedUserId: string) => {
        const resp = await loginAsUser({
          userId: selectedUserId,
        })
        if (!resp.data.data) throw new Error()

        return { token: resp.data.data.token, user: resp.data.data.user }
      },
      onSuccess: (data) => {
        const { token, user } = data
        const userId = user._id

        setSecureBearerCookies(token, TWILIGHT_PREFIX)
        setSecureUserIdCookie(userId, TWILIGHT_PREFIX)
        navigate('/')
        window.location.reload()
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })
  const signInAsUser = async (): Promise<void> => {
    if (selectedUser) {
      mutateLoginAsUser(selectedUser._id)
    }
  }

  const { logoutTwilightUser } = useAuth()

  const loadingFetch = isFetchingUsers
  const loadingLoginAsUser = isLoggingInAsUser

  const { data: isTwilightUser } = useQuery(authQueries.twilightZone())
  if (!isBoappaSuperadmin && !isTwilightUser) {
    return null
  }

  return (
    <div className="space-y-4">
      {isTwilightUser ? (
        <Button
          className="max-h-12"
          variant={ButtonVariant.PRIMARY}
          label={translate('boappaSuperadmin.logout')}
          onClick={() => logoutTwilightUser(navigate)}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <div className="flex flex-col gap-4">
            <div className="flex items-end gap-1">
              <Controller
                name="query"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SearchBar
                    placeholder={translate('boappaSuperadmin.labels.search')}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <Button
                type="submit"
                className="max-h-12"
                loading={loadingFetch}
                disabled={loadingFetch || !isValid}
                icon={IconChoices.SEARCH}
              />
            </div>
            {listUsers && listUsers.length > 0 && (
              <>
                <p style={theme.textVariants.baseBold}>
                  {translate('boappaSuperadmin.labels.matches')}
                </p>
                <div>
                  {listUsers.map((user) => (
                    <button
                      key={user._id}
                      className={`flex max-h-12 w-72 cursor-pointer items-center justify-center
                      rounded-3xl border p-4 shadow-md hover:bg-neutral-96
                      ${selectedUser?._id === user._id ? 'bg-neutral-90' : ''}`}
                      onClick={(e) => onUserClick(e, user)}
                    >
                      {getUserFullName(user)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </form>
      )}
      {selectedUser && (
        <HorizontalFormSection
          title={translate('boappaSuperadmin.userTitle')}
          icon={IconChoices.PERSON}
          iconPlacement="left"
        >
          <p className="flex gap-1">
            <span style={theme.textVariants.baseBold}>
              {translate('boappaSuperadmin.labels.firstname')}
            </span>
            {selectedUser.name}
          </p>
          <p className="flex gap-1">
            <span style={theme.textVariants.baseBold}>
              {translate('boappaSuperadmin.labels.lastname')}
            </span>
            {selectedUser.surname}
          </p>
          <p className="flex gap-1">
            <span style={theme.textVariants.baseBold}>
              {translate('boappaSuperadmin.labels.verified')}
            </span>
            <Icon
              icon={
                selectedUser.isVerified
                  ? IconChoices.CHECKMARK
                  : IconChoices.CLOSE_CROSS
              }
            />
          </p>
          <p className="flex gap-1">
            <span style={theme.textVariants.baseBold}>
              {translate('boappaSuperadmin.labels.email')}
            </span>
            {selectedUser.email}
          </p>
          <Button
            className="max-h-12"
            variant={ButtonVariant.PRIMARY}
            label={translate('boappaSuperadmin.signin')}
            onClick={signInAsUser}
            loading={loadingLoginAsUser}
          />
        </HorizontalFormSection>
      )}
    </div>
  )
}
